/**
 * The degraded-state banners that sit under the navbar on every screen
 * (FR-016, FR-017).
 *
 * What the active server refuses shapes the whole app, not one screen, so the
 * explanation belongs in the shell: whichever screen the user is on, the reason
 * a control is missing is above it, with a way to a server that does support it
 * (Principle V, scenarios 8 and 9).
 *
 * Nothing is said about a server whose capabilities have not been checked yet,
 * and nothing is said when there is no active connection at all.
 *
 * The visual language is the prototype's builder-nopreview banner.
 *
 * @author John Grimes
 */

import { AlertIcon } from "@primer/octicons-react";
import { Link } from "react-router";

import {
  connectionLimits,
  useConnections,
  useSettings,
} from "../features/connections/useConnections";

import type { JSX } from "react";

/**
 * Explains what the active connection's server will not do.
 *
 * @returns The banners, or nothing when the active server supports everything.
 *
 * @example
 * ```tsx
 * <AppNavbar username={username} />
 * <ConnectionBanners />
 * ```
 */
export const ConnectionBanners = (): JSX.Element | undefined => {
  const { connections } = useConnections();
  const { settings } = useSettings();

  const active = connections.find(
    (connection) => connection.id === settings?.activeConnectionId,
  );
  const limits = connectionLimits(active);
  if (!limits.cannotRunPreviews && !limits.cannotSave) return undefined;

  return (
    <>
      {limits.cannotRunPreviews && (
        <div
          role="alert"
          className="alert alert-warning rounded-none border-x-0 text-sm px-3 sm:px-5 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2"
        >
          <AlertIcon size={16} className="shrink-0" aria-hidden />
          <span className="flex-1 min-w-0">
            <strong>This server can&apos;t run previews.</strong> You can still
            build and save views here, but previews and Run & download are
            turned off.
          </span>
          <Link to="/connections" className="btn">
            Choose another server
          </Link>
        </div>
      )}
      {limits.cannotSave && (
        <div
          role="alert"
          className="alert alert-warning rounded-none border-x-0 text-sm px-3 sm:px-5 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2"
        >
          <AlertIcon size={16} className="shrink-0" aria-hidden />
          <span className="flex-1 min-w-0">
            <strong>You can&apos;t save views to this server.</strong> Your
            sign-in there does not allow it. You can still build and preview,
            and download a view as a JSON file to keep your work.
          </span>
          <Link to="/connections" className="btn">
            Choose another server
          </Link>
        </div>
      )}
    </>
  );
};
